import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FooterSection = styled.footer`
  background: ${props => props.theme.colors.darkGray};
  border-top: 1px solid ${props => props.theme.colors.mediumGray};
  padding: 60px 0 30px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 40px 0 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 60px;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    text-align: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const MobileFooter = styled.div`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: block;
    text-align: center;
    margin-bottom: 30px;
  }
`;

const FooterBrand = styled.div``;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const LogoImg = styled.img`
  height: 50px;
  width: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

const LogoText = styled.h3`
  font-family: ${props => props.theme.fonts.display};
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const BrandDescription = styled.p`
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.6;
  margin-bottom: 24px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: 16px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px) scale(1.1);
  }
`;

const FooterColumn = styled.div``;

const ColumnTitle = styled.h4`
  color: ${props => props.theme.colors.white};
  font-weight: 600;
  margin-bottom: 20px;
  font-size: 1.1rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 12px;
    font-size: 1rem;
  }
`;

const FooterLink = styled.a`
  display: block;
  color: ${props => props.theme.colors.lightGray};
  text-decoration: none;
  margin-bottom: 12px;
  transition: color 0.3s ease;
  font-size: 0.95rem;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 8px;
    font-size: 0.9rem;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${props => props.theme.colors.mediumGray};
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.9rem;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 12px;
  }
`;

const LegalLink = styled.a`
  color: ${props => props.theme.colors.lightGray};
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const Footer = () => {
  return (
    <FooterSection>
      <Container>
        <FooterContent>
          <FooterBrand>
            <Logo>
              <LogoImg src="/Perfil.jpg" alt="Leonardo Celini" />
              <LogoText>LEONARDO CELINI</LogoText>
            </Logo>
            <BrandDescription>
              Especialista em consignação de veículos com mais de 15 anos de experiência. 
              Transformando lojistas em verdadeiras máquinas de lucro através de estratégias 
              comprovadas e metodologias exclusivas.
            </BrandDescription>
            <SocialLinks>
              <SocialLink 
                href="https://www.instagram.com/leocelinii" 
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <img src="/LogoInsta.png" alt="Instagram" style={{width: '24px', height: '24px'}} />
              </SocialLink>
              <SocialLink 
                href="https://www.youtube.com/@lmpodcastt" 
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <img src="/LogoYoutube.png" alt="YouTube" style={{width: '24px', height: '24px'}} />
              </SocialLink>
            </SocialLinks>
          </FooterBrand>

          <FooterColumn>
            <ColumnTitle>Curso</ColumnTitle>
            <FooterLink href="#about">Sobre o Curso</FooterLink>
            <FooterLink href="#benefits">Benefícios</FooterLink>
            <FooterLink href="#pricing">Preços</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Suporte</ColumnTitle>
            <FooterLink href="#faq">FAQ</FooterLink>
            <FooterLink href="#">Contato</FooterLink>
            <FooterLink href="#">Central de Ajuda</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Leonardo Celini</ColumnTitle>
            <FooterLink href="#about">Sobre Leonardo</FooterLink>
            <FooterLink href="#">Consultoria</FooterLink>
            <FooterLink href="#">Mentoria</FooterLink>
          </FooterColumn>
        </FooterContent>

        <MobileFooter>
          <Logo>
            <LogoImg src="/Perfil.jpg" alt="Leonardo Celini" />
            <LogoText>LEONARDO CELINI</LogoText>
          </Logo>
          <SocialLinks>
            <SocialLink href="https://www.instagram.com/leocelinii" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <img src="/LogoInsta.png" alt="Instagram" style={{width: '24px', height: '24px'}} />
            </SocialLink>
            <SocialLink href="https://www.youtube.com/@lmpodcastt" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <img src="/LogoYoutube.png" alt="YouTube" style={{width: '24px', height: '24px'}} />
            </SocialLink>
          </SocialLinks>
        </MobileFooter>

        <FooterBottom>
          <Copyright>
            © 2025 Leonardo Celini. Todos os direitos reservados.
          </Copyright>
          <LegalLinks>
            <LegalLink href="#">Termos de Uso</LegalLink>
            <LegalLink href="#">Política de Privacidade</LegalLink>
            <LegalLink href="#">Política de Reembolso</LegalLink>
          </LegalLinks>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer;